'use client';

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  type ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from '@repo/ui';
import type { JSX } from 'react';
import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from 'recharts';

const config: ChartConfig = {
  reviews: { label: 'Reviews', color: 'var(--chart-1)' },
};

function formatHour(hour: number): string {
  const period = hour < 12 ? 'a' : 'p';
  const display = hour % 12 === 0 ? 12 : hour % 12;
  return `${display}${period}`;
}

export function PracticeTimeChart({
  practiceHours,
}: {
  practiceHours: Record<string, number>;
}): JSX.Element {
  const data = Array.from({ length: 24 }, (_, hour) => ({
    hour,
    reviews: practiceHours[String(hour)] ?? 0,
  }));
  const hasData = data.some((d) => d.reviews > 0);

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-lg">Practice Hours</CardTitle>
      </CardHeader>
      <CardContent>
        {!hasData ? (
          <div className="text-muted-foreground py-8 text-center text-sm">No data yet</div>
        ) : (
          <ChartContainer config={config} className="h-[240px] w-full">
            <BarChart data={data} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
              <defs>
                <linearGradient id="fill-reviews" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="var(--color-reviews)" stopOpacity={0.9} />
                  <stop offset="100%" stopColor="var(--color-reviews)" stopOpacity={0.35} />
                </linearGradient>
              </defs>
              <CartesianGrid vertical={false} strokeDasharray="3 3" className="stroke-border/30" />
              <XAxis
                dataKey="hour"
                tickLine={false}
                axisLine={false}
                tickMargin={8}
                interval={2}
                tickFormatter={(v) => formatHour(Number(v))}
              />
              <YAxis width={28} tickLine={false} axisLine={false} allowDecimals={false} />
              <ChartTooltip
                content={
                  <ChartTooltipContent
                    labelFormatter={(_, p) => formatHour(p?.[0]?.payload?.hour ?? 0)}
                  />
                }
              />
              <Bar dataKey="reviews" fill="url(#fill-reviews)" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ChartContainer>
        )}
      </CardContent>
    </Card>
  );
}
